import { type FC, useEffect, useState } from 'react';

import { isDefined, isNotDefined } from '@enterprise/core';

import type { PortalItemProps } from './types/portal-item.props';

import { DEFAULT_PORTAL_ID } from './constants';
import { Portal } from './portal.component';

/**
 * Renders its children into a specified DOM node using a portal.
 *
 * @remarks
 * This component locates a DOM element by `portalOutletId` (defaulting to `DEFAULT_PORTAL_ID`)
 * and renders its children into that element using the `Portal` component.
 * If the target element is not found, nothing is rendered.
 *
 * @param props - The props for the PortalItem component.
 * @param props.children - The React nodes to render inside the portal.
 * @param props.portalOutletId - The id of the DOM element to use as the portal outlet. Defaults to `DEFAULT_PORTAL_ID`.
 *
 * @returns The portaled children, or `null` if the outlet is not found.
 */
export const PortalItem: FC<PortalItemProps> = ({ children, portalOutletId = DEFAULT_PORTAL_ID }) => {
  const [portalOutlet, setPortalOutlet] = useState<HTMLElement>();

  useEffect(() => {
    const element = document.getElementById(portalOutletId);

    if (isDefined(element)) {
      setPortalOutlet(element);
    }
  }, [portalOutletId]);

  if (isNotDefined(portalOutlet)) {
    return null;
  }

  return <Portal node={portalOutlet}>{children}</Portal>;
};
